// src/components/admin/blog/CategoryManager.jsx
import React, { useState, useEffect } from 'react';
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiX,
  FiCheck,
  FiFolder,
} from 'react-icons/fi';
import { blogService } from '../../../services/blogService';
import { useToast } from '../../../hooks/useToast';

export const CategoryManager = ({
  categories: externalCategories,
  onCategoriesUpdate,
}) => {
  const [categories, setCategories] = useState(externalCategories || []);
  const [showModal, setShowModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (externalCategories) {
      setCategories(externalCategories);
    }
  }, [externalCategories]);

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      showToast('Please enter a category name', 'error');
      return;
    }

    // Check for duplicate
    if (
      categories.some(
        c => c.name.toLowerCase() === newCategory.trim().toLowerCase(),
      )
    ) {
      showToast('Category already exists', 'error');
      return;
    }

    setLoading(true);
    try {
      const newCat = { name: newCategory.trim(), count: 0 };
      const updatedCategories = [...categories, newCat];
      setCategories(updatedCategories);
      localStorage.setItem(
        'blog_categories',
        JSON.stringify(updatedCategories),
      );
      setNewCategory('');
      onCategoriesUpdate?.(updatedCategories);
      showToast('Category added successfully', 'success');
    } catch (error) {
      console.error('Failed to add category:', error);
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async categoryName => {
    if (
      window.confirm(
        `Delete category "${categoryName}"? This will not delete blog posts.`,
      )
    ) {
      const updatedCategories = categories.filter(c => c.name !== categoryName);
      setCategories(updatedCategories);
      localStorage.setItem(
        'blog_categories',
        JSON.stringify(updatedCategories),
      );
      onCategoriesUpdate?.(updatedCategories);
      showToast('Category deleted successfully', 'success');
    }
  };

  const handleEditCategory = async (oldName, newName) => {
    if (!newName.trim() || newName === oldName) {
      setEditingCategory(null);
      return;
    }

    // Check for duplicate
    if (
      categories.some(
        c =>
          c.name.toLowerCase() === newName.trim().toLowerCase() &&
          c.name !== oldName,
      )
    ) {
      showToast('Category already exists', 'error');
      return;
    }

    const updatedCategories = categories.map(c =>
      c.name === oldName ? { ...c, name: newName.trim() } : c,
    );
    setCategories(updatedCategories);
    localStorage.setItem('blog_categories', JSON.stringify(updatedCategories));
    onCategoriesUpdate?.(updatedCategories);
    setEditingCategory(null);
    showToast('Category updated successfully', 'success');
  };

  return (
    <div className="mt-2">
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-1 text-sm hover:underline transition-colors"
        style={{ color: 'var(--muted)' }}
      >
        <FiPlus className="w-3 h-3" />
        Manage Categories
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className="relative w-full max-w-md rounded-xl shadow-2xl"
            style={{ backgroundColor: 'var(--bg)' }}
          >
            <div
              className="p-4 border-b flex items-center justify-between"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center gap-2">
                <FiFolder
                  className="w-5 h-5"
                  style={{ color: 'var(--text)' }}
                />
                <h3 className="font-semibold" style={{ color: 'var(--text)' }}>
                  Manage Categories
                </h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newCategory}
                  onChange={e => setNewCategory(e.target.value)}
                  placeholder="New category name"
                  className="flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                  onKeyPress={e => e.key === 'Enter' && handleAddCategory()}
                />
                <button
                  onClick={handleAddCategory}
                  disabled={loading || !newCategory.trim()}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 hover:opacity-90"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  Add
                </button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {categories.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>
                      No categories yet. Create your first category above.
                    </p>
                  </div>
                ) : (
                  categories.map(category => (
                    <div
                      key={category.name}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    >
                      {editingCategory === category.name ? (
                        <div className="flex-1 flex items-center gap-2">
                          <input
                            type="text"
                            defaultValue={category.name}
                            className="flex-1 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                            style={{
                              borderColor: 'var(--border)',
                              color: 'var(--text)',
                            }}
                            autoFocus
                            onKeyPress={e => {
                              if (e.key === 'Enter') {
                                handleEditCategory(
                                  category.name,
                                  e.target.value,
                                );
                              }
                            }}
                          />
                          <button
                            onClick={e => {
                              const input = e.currentTarget.previousSibling;
                              handleEditCategory(category.name, input.value);
                            }}
                            className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors"
                          >
                            <FiCheck className="w-4 h-4 text-green-500" />
                          </button>
                          <button
                            onClick={() => setEditingCategory(null)}
                            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <FiX className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span style={{ color: 'var(--text)' }}>
                            {category.name}
                            <span
                              className="ml-2 text-xs"
                              style={{ color: 'var(--muted)' }}
                            >
                              ({category.count} posts)
                            </span>
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setEditingCategory(category.name)}
                              className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteCategory(category.name)
                              }
                              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors text-red-500"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
